import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import UserMenu from './UserMenu';

const SolicitationForm = ({ userName, onLogout }) => {
  const navigate = useNavigate();
  
  // Estado dos campos do formulário
  const [setores, setSetores] = useState([]);
  const [gestores, setGestores] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [itens, setItens] = useState([]);

  // Carregar valores persistidos do localStorage
  const [selectedSetor, setSelectedSetor] = useState(() => {
    const saved = localStorage.getItem('selectedSetor');
    return saved ? JSON.parse(saved) : null;
  });
  
  const [selectedGestor, setSelectedGestor] = useState(() => {
    const saved = localStorage.getItem('selectedGestor');
    return saved ? JSON.parse(saved) : null;
  });
  
  const [isNovoColaborador, setIsNovoColaborador] = useState(() => {
    const saved = localStorage.getItem('isNovoColaborador');
    return saved ? JSON.parse(saved) : false;
  });
  
  const [centroDeCusto, setCentroDeCusto] = useState(() => {
    // Só carrega do localStorage se for novo colaborador
    const isNovo = localStorage.getItem('isNovoColaborador');
    if (isNovo === 'true') {
      return localStorage.getItem('centroDeCustoNovo') || '';
    }
    return ''; // Para colaborador existente, sempre começa vazio ao recarregar
  });

  const [selectedFuncionario, setSelectedFuncionario] = useState(null);
  const [selectedCargo, setSelectedCargo] = useState(null);
  
  const [selectedItens, setSelectedItens] = useState({}); // { itemId: { selected: true, permissao: 'VISUALIZAR' } }

  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Código QLP gerado para novo colaborador (mantém o mesmo durante a sessão)
  const [codigoQLPGerado, setCodigoQLPGerado] = useState('');


  // Persistir no localStorage quando os valores mudarem
  useEffect(() => {
    if (selectedSetor) {
      localStorage.setItem('selectedSetor', JSON.stringify(selectedSetor));
    } else {
      localStorage.removeItem('selectedSetor');
    }
  }, [selectedSetor]);

  useEffect(() => {
    if (selectedGestor) {
      localStorage.setItem('selectedGestor', JSON.stringify(selectedGestor));
    } else {
      localStorage.removeItem('selectedGestor');
    }
  }, [selectedGestor]);

  useEffect(() => {
    localStorage.setItem('isNovoColaborador', JSON.stringify(isNovoColaborador));
  }, [isNovoColaborador]);

  // Persistir centro de custo apenas para novo colaborador
  useEffect(() => {
    if (isNovoColaborador && centroDeCusto) {
      localStorage.setItem('centroDeCustoNovo', centroDeCusto);
    } else if (!isNovoColaborador) {
      localStorage.removeItem('centroDeCustoNovo');
    }
  }, [centroDeCusto, isNovoColaborador]);

  useEffect(() => {
    loadSetores();
    loadItens();
  }, []);

  useEffect(() => {
    if (selectedSetor) {
      loadGestores(selectedSetor.label);
      loadFuncionarios(selectedSetor.value);
      loadCargos(selectedSetor.value);
      
      // Gerar código QLP quando seleciona setor (se for novo colaborador)
      if (isNovoColaborador) {
        gerarNovoCodigoQLP();
      }
    } else {
      setGestores([]);
      setFuncionarios([]);
      setCargos([]);
      setSelectedGestor(null);
      handleFuncionarioChange(null);
      setCodigoQLPGerado('');
    }
  }, [selectedSetor, isNovoColaborador]);

  // Limpar campos quando o setor mudar
  useEffect(() => {
    // Limpar campos quando o setor for alterado
    setSelectedGestor(null);
    handleFuncionarioChange(null);
    setCentroDeCusto('');
    setCodigoQLPGerado('');
    
    // Limpar também do localStorage
    localStorage.removeItem('selectedGestor');
    localStorage.removeItem('centroDeCustoNovo');
  }, [selectedSetor]);

  useEffect(() => {
    if (!isNovoColaborador && selectedFuncionario) {
      const funcionario = funcionarios.find(f => f.value === selectedFuncionario.value);
      if (funcionario?.cargoId) {
        const cargoOption = cargos.find(c => c.value === funcionario.cargoId);
        setSelectedCargo(cargoOption || null);
      }
      // Preencher centro de custo do colaborador como sugestão
      if (funcionario?.centroDeCusto) {
        setCentroDeCusto(funcionario.centroDeCusto);
      } else {
        setCentroDeCusto(''); // Limpa se não tiver centro de custo
      }
    }
  }, [selectedFuncionario, funcionarios, cargos, isNovoColaborador]);

  const loadSetores = async () => {
    try {
      const data = await apiService.getSetores();
      // data já é um array, não precisa verificar data.results
      const options = data.map(s => ({ value: s.id, label: s.nome }));
      setSetores(options);
    } catch (error) {
      toast.error('Erro ao carregar setores');
    }
  };

  const loadGestores = async (setorNome) => {
    try {
      const data = await apiService.getGestores(setorNome);
      // data já é um array, não precisa verificar data.results
      const options = data.map(g => ({ value: g.id, label: g.nome_completo }));
      setGestores(options);
      
      // Selecionar automaticamente o gestor se houver apenas um
      if (options.length === 1) {
        setSelectedGestor(options[0]);
        localStorage.setItem('selectedGestor', JSON.stringify(options[0]));
      } else if (options.length > 1) {
        setSelectedGestor(null);
        localStorage.removeItem('selectedGestor');
      } else {
        setSelectedGestor(null);
        localStorage.removeItem('selectedGestor');
      }
    } catch (error) {
      toast.error('Erro ao carregar gestores');
    }
  };

  const loadFuncionarios = async (setorId) => {
    try {
      const data = await apiService.getFuncionarios(setorId);
      // data já é um array, não precisa verificar data.results
      const options = data.map(f => ({
        value: f.id,
        label: f.nome_completo,
        cargo: f.cargo.nome,
        cargoId: f.cargo.id,
        centroDeCusto: f.centro_de_custo
      }));
      setFuncionarios(options);
    } catch (error) {
      toast.error('Erro ao carregar funcionários');
    }
  };

  const loadCargos = async (setorId) => {
    try {
      const data = await apiService.getCargos(setorId);
      // data já é um array, não precisa verificar data.results
      const options = data.map(c => ({ value: c.id, label: c.nome }));
      setCargos(options);
    } catch (error) {
      toast.error('Erro ao carregar cargos');
    }
  };

  const loadItens = async () => {
    try {
      const data = await apiService.getItensSolicitaveis();
      setItens(data);
    } catch (error) {
      toast.error('Erro ao carregar itens solicitáveis');
    }
  };

  const gerarNovoCodigoQLP = async () => {
    try {
      if (!selectedSetor) return;
      
      const data = await apiService.getProximoNumeroQLP(selectedSetor.label);
      const nomeSetor = selectedSetor.label.toUpperCase();
      const numero = data.proximo_numero;
      
      // Formato: QLPSETORNUMERO (ex: QLPLOGISTICA1, QLPCOMERCIAL2)
      setCodigoQLPGerado(`QLP${nomeSetor.replace(/\s+/g, '').replace(/-/g, '')}${numero}`);
    } catch (error) {
      // Fallback sequencial simples
      const nomeSetor = selectedSetor.label.toUpperCase();
      setCodigoQLPGerado(`QLP${nomeSetor.replace(/\s+/g, '').replace(/-/g, '')}1`);
    }
  };

  const handleItemToggle = (itemId, isERP) => {
    setSelectedItens(prev => {
      const newState = { ...prev };
      if (newState[itemId]?.selected) {
        delete newState[itemId];
      } else {
        newState[itemId] = { 
          selected: true, 
          permissao: isERP ? 'EDITAR' : 'N/A' // Mudado de 'VISUALIZAR' para 'EDITAR'
        };
      }
      return newState;
    });
  };

  const handleFuncionarioChange = (funcionario) => {
    setSelectedFuncionario(funcionario);
    
    // Se um funcionário foi selecionado, preencher automaticamente o cargo
    if (funcionario && funcionario.cargo) {
      const cargoCorrespondente = cargos.find(c => c.label === funcionario.cargo);
      if (cargoCorrespondente) {
        setSelectedCargo(cargoCorrespondente);
      }
    } else {
      // Se nenhum funcionário foi selecionado, limpar o cargo
      setSelectedCargo(null);
    }
  };

  const handlePermissaoChange = (itemId, permissao) => {
    setSelectedItens(prev => {
      // Sempre define a permissão clicada (comportamento de radio button)
      // Quando clica em uma, a outra automaticamente desmarca
      return {
      ...prev,
      [itemId]: { ...prev[itemId], permissao }
      };
    });
  };

  const calcularTotais = () => {
    const itensSelecionados = Object.keys(selectedItens)
      .map(id => itens.find(item => item.id === parseInt(id)))
      .filter(Boolean);
    
    const custoEquipamentos = itensSelecionados
      .filter(item => item.tipo === 'EQUIPAMENTO')
      .reduce((sum, item) => sum + parseFloat(item.preco || 0), 0);
    
    const custoMensalLicencas = itensSelecionados
      .filter(item => item.tipo === 'LICENCA_ERP' || item.tipo === 'LICENCA_OUTRAS')
      .reduce((sum, item) => sum + parseFloat(item.preco || 0), 0);
    
    const custoAnualLicencas = custoMensalLicencas * 12;
    
    return {
      custoEquipamentos,
      custoMensalLicencas,
      custoAnualLicencas
    };
  };

  const totais = calcularTotais();

  const validateForm = () => {
    if (!selectedSetor) {
      toast.error('Selecione um setor');
      return false;
    }
      if (!selectedCargo) {
      toast.error('Selecione um cargo');
        return false;
      }
    if (!isNovoColaborador && !selectedFuncionario) {
        toast.error('Selecione um funcionário');
        return false;
    }
    if (!centroDeCusto || centroDeCusto.length !== 4 || !/^\d+$/.test(centroDeCusto)) {
      toast.error('Centro de custo deve conter exatamente 4 dígitos numéricos');
      return false;
    }
    if (Object.keys(selectedItens).length === 0) {
      toast.error('Selecione pelo menos um item');
      return false;
    }

    // Validar permissões ERP
    for (const [itemId, itemData] of Object.entries(selectedItens)) {
      const item = itens.find(i => i.id === parseInt(itemId));
      if (item?.tipo === 'LICENCA_ERP') {
        if (!itemData.permissao || itemData.permissao === 'N/A') {
          toast.error(`Selecione a permissão para a licença ERP: ${item.nome}`);
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      
      // Preparar dados do relatório no formato do CSV
      const itensSelecionados = Object.keys(selectedItens)
        .map(id => itens.find(item => item.id === parseInt(id)))
        .filter(Boolean);
      
      const licencas = itensSelecionados.filter(item => 
        item.tipo === 'LICENCA_ERP' || item.tipo === 'LICENCA_OUTRAS'
      );
      const equipamentos = itensSelecionados.filter(item => 
        item.tipo === 'EQUIPAMENTO'
      );

      // Preparar listas de licenças
      const licencasLista = licencas.map(item => item.nome).join(', ');
      const licencasComPermissoes = licencas.map(item => {
        const permissao = selectedItens[item.id]?.permissao || 'N/A';
        return `${item.nome} (${permissao})`;
      }).join(', ');

      // Preparar lista de equipamentos
      const equipamentosLista = equipamentos.map(item => item.nome).join(', ');

      // Calcular custos
      const custoHardware = equipamentos.reduce((sum, item) => sum + parseFloat(item.preco || 0), 0);
      const custoMensalLicencas = licencas.reduce((sum, item) => sum + parseFloat(item.preco || 0), 0);
      const custoAnualLicencas = custoMensalLicencas * 12;
      const custoTotal = custoHardware + custoAnualLicencas;

      // Determinar tipo de colaborador
      const tipoColaborador = isNovoColaborador ? 'NOVO (QLP)' : 'EXISTENTE';

      // Preparar dados do relatório
      const relatorioData = {
        gestor_nome: selectedGestor.label,
        funcionario_nome: isNovoColaborador ? codigoQLPGerado : selectedFuncionario.label,
        funcionario_cargo: selectedCargo.label,
        funcionario_setor: selectedSetor.label,
        centro_custo: centroDeCusto,
        solicitante_usuario: userName,
        licencas_lista: licencasLista,
        licencas_com_permissoes: licencasComPermissoes,
        equipamentos_lista: equipamentosLista,
        custo_hardware: custoHardware,
        custo_mensal_licencas: custoMensalLicencas,
        custo_anual_licencas: custoAnualLicencas,
        custo_total: custoTotal,
        tipo_colaborador: tipoColaborador,
        tem_licencas: licencas.length > 0 ? 'SIM' : 'NÃO',
        tem_equipamentos: equipamentos.length > 0 ? 'SIM' : 'NÃO',
        quantidade_itens: itensSelecionados.length
      };

      // Criar relatório no backend
      const resultado = await apiService.createRelatorio(relatorioData);
      
      toast.success(`Solicitação criada com sucesso!`);

      // Limpa apenas os campos que não devem persistir
      handleFuncionarioChange(null);
      setSelectedItens({});
      
      // Para novo colaborador, gera novo código QLP mas mantém centro de custo
      if (isNovoColaborador) {
        // Aguardar um pouco para garantir que o registro foi salvo no banco
        setTimeout(() => {
          gerarNovoCodigoQLP();
        }, 1000);
        // Mantém centroDeCusto
      } else {
        setCentroDeCusto('');
      }
      
      // Não limpa: selectedSetor, selectedGestor, isNovoColaborador
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao criar relatório');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Separar itens por categoria
  const licencasERPList = itens.filter(item => item.tipo === 'LICENCA_ERP');
  const outrasLicencas = itens.filter(item => item.tipo === 'LICENCA_OUTRAS');
  const equipamentos = itens.filter(item => item.tipo === 'EQUIPAMENTO');

  const isFormValid = selectedSetor && selectedCargo && 
                      (isNovoColaborador || selectedFuncionario) &&
                      centroDeCusto && centroDeCusto.length === 4 && /^\d+$/.test(centroDeCusto) && 
                      Object.keys(selectedItens).length > 0;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };


  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: '8px',
      borderWidth: '1px',
      borderColor: state.isFocused ? '#6b7280' : state.isDisabled ? '#e5e7eb' : 'transparent',
      minHeight: '38px',
      boxShadow: state.isFocused 
        ? '0 0 0 3px rgba(107, 114, 128, 0.1)' 
        : 'none',
      transition: 'all 0.15s ease',
      backgroundColor: state.isDisabled ? '#f9fafb' : 'white',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      '&:hover': {
        borderColor: '#6b7280'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#6b7280' 
        : state.isFocused 
          ? '#f3f4f6' 
          : 'white',
      color: state.isSelected ? 'white' : '#1f2937',
      cursor: 'pointer',
      padding: '8px 12px',
      fontWeight: state.isSelected ? '600' : '400',
      fontSize: '14px',
      '&:active': {
        backgroundColor: '#6b7280'
      }
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '8px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      border: '1px solid #e5e7eb',
      overflow: 'hidden',
      zIndex: 9999
    }),
    menuList: (base) => ({
      ...base,
      padding: '4px',
      maxHeight: '200px'
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    })
  };

  const cargoSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: '8px',
      borderWidth: '1px',
      borderColor: state.isFocused ? '#3b82f6' : state.isDisabled ? '#e5e7eb' : 'transparent',
      minHeight: '38px',
      boxShadow: state.isFocused
        ? '0 0 0 3px rgba(59, 130, 246, 0.1)'
        : 'none',
      transition: 'all 0.15s ease',
      backgroundColor: state.isDisabled ? '#f9fafb' : '#f9fafb',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#3b82f6'
      }
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    })
  };

  // Estilos específicos para cada campo
  const setorSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: '8px',
      borderWidth: '1px',
      borderColor: state.isFocused ? '#3b82f6' : state.isDisabled ? '#e5e7eb' : 'transparent',
      minHeight: '38px',
      boxShadow: state.isFocused
        ? '0 0 0 3px rgba(59, 130, 246, 0.1)'
        : 'none',
      transition: 'all 0.15s ease',
      backgroundColor: state.isDisabled ? '#f9fafb' : '#f9fafb',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#3b82f6'
      }
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    })
  };

  const gestorSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: '8px',
      borderWidth: '1px',
      borderColor: state.isFocused ? '#3b82f6' : state.isDisabled ? '#e5e7eb' : 'transparent',
      minHeight: '38px',
      boxShadow: state.isFocused
        ? '0 0 0 3px rgba(59, 130, 246, 0.1)'
        : 'none',
      transition: 'all 0.15s ease',
      backgroundColor: state.isDisabled ? '#f9fafb' : '#f9fafb',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#3b82f6'
      }
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    })
  };

  const nomeSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: '8px',
      borderWidth: '1px',
      borderColor: state.isFocused ? '#3b82f6' : state.isDisabled ? '#e5e7eb' : 'transparent',
      minHeight: '38px',
      boxShadow: state.isFocused
        ? '0 0 0 3px rgba(59, 130, 246, 0.1)'
        : 'none',
      transition: 'all 0.15s ease',
      backgroundColor: state.isDisabled ? '#f9fafb' : '#f9fafb',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#3b82f6'
      }
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    })
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header Compacto */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          {/* Título da página à esquerda */}
          <div className="flex-1">
            <h1 className="text-xl font-semibold text-gray-900" style={{ color: '#111827' }}>
              Solicitar Recursos
            </h1>
            <p className="text-sm text-gray-600 font-medium">Licenças & Equipamentos • CEDISA</p>
          </div>
          
          {/* Logo CEDISA centralizada (botão para home) */}
          <div className="flex-1 flex justify-center">
            <button 
              onClick={() => navigate('/')}
              className="hover:opacity-80 transition-opacity duration-200"
            >
              <img src="cedisa-logo.png" alt="Cedisa Logo" className="h-10 drop-shadow-sm" />
            </button>
          </div>
          
          {/* Nome do usuário na direita */}
          <div className="flex-1 flex justify-end">
            <UserMenu userName={userName} onLogout={onLogout} />
          </div>
        </div>
      </div>

      {/* Aviso Importante */}
      <div className="bg-red-50 border-l-4 border-red-400 p-4 mx-4 mt-4 rounded-r-lg shadow-sm">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700 font-semibold">
              <strong>ATENÇÃO:</strong> Esta é uma <strong>SOLICITAÇÃO</strong> de recursos. 
              O envio desta solicitação <strong>NÃO GARANTE</strong> que será aprovada pela CEDISA. 
              Todas as solicitações passam por análise e aprovação dos responsáveis.
            </p>
          </div>
        </div>
      </div>

      {/* Formulário Otimizado para Notebook */}
      <div className="flex-1 overflow-hidden">
        <form onSubmit={handleSubmit} className="h-full bg-white flex flex-col">
          {/* Grid Principal - Conteúdo em 2 Colunas Compactas */}
          <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-3">
            
            {/* COLUNA ESQUERDA - Informações da Solicitação (33%) */}
            <div className="lg:col-span-1 border-r border-gray-200 flex flex-col overflow-hidden bg-gradient-to-b from-gray-50 to-white animate-slide-in-left">
              <div className="p-4 flex flex-col overflow-hidden">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                  </div>
                    <h3 className="text-lg font-semibold text-gray-900">Informações da Solicitação</h3>
                </div>
                  <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                    <button
                      type="button"
                      onClick={() => {
                        setIsNovoColaborador(false);
                        setSelectedCargo(null);
                        setCodigoQLPGerado('');
                        setCentroDeCusto('');
                      }}
                      className={`px-4 py-2.5 rounded-md text-sm font-semibold transition-all duration-150 ease-out ${
                        !isNovoColaborador
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Existente
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsNovoColaborador(true);
                        handleFuncionarioChange(null);
                        const savedCentroDeCusto = localStorage.getItem('centroDeCustoNovo');
                        if (savedCentroDeCusto) {
                          setCentroDeCusto(savedCentroDeCusto);
                        } else {
                          setCentroDeCusto('');
                        }
                      }}
                      className={`px-4 py-2.5 rounded-md text-sm font-semibold transition-all duration-150 ease-out ${
                        isNovoColaborador
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Novo
                    </button>
                  </div>
                </div>

                <div className="space-y-6 flex-1 overflow-y-auto">
                  {/* Setor */}
                  <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-300 p-5 shadow-sm form-field transition-all duration-150 ease-out">
                    <label className="block text-base font-medium text-blue-600 mb-2.5 flex items-center">
                      <svg className="w-4 h-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      Setor <span className="text-red-500 ml-1 text-sm">*</span>
                    </label>
                    <Select
                      value={selectedSetor}
                      onChange={(value) => {
                        setSelectedSetor(value);
                        // Remove seleção do campo após seleção
                        setTimeout(() => {
                          const activeElement = document.activeElement;
                          if (activeElement && activeElement.blur) {
                            activeElement.blur();
                          }
                          // Remove foco de qualquer elemento com classe do react-select
                          const selectElements = document.querySelectorAll('[class*="css-"]');
                          selectElements.forEach(el => {
                            if (el.blur) el.blur();
                          });
                        }, 150);
                      }}
                      options={setores}
                      placeholder="Selecione o setor..."
                      className="text-sm"
                      isClearable
                      isSearchable
                      menuPortalTarget={document.body}
                      styles={setorSelectStyles}
                    />
                  </div>

                  {/* Gestor Responsável */}
                  <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-300 p-5 shadow-sm form-field transition-all duration-150 ease-out">
                    <label className="block text-base font-medium text-blue-600 mb-2.5 flex items-center">
                      <svg className="w-4 h-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Gestor Responsável <span className="text-red-500 ml-1 text-sm">*</span>
                    </label>
                    <Select
                      value={selectedGestor}
                      onChange={(value) => {
                        setSelectedGestor(value);
                        // Remove seleção do campo após seleção
                        setTimeout(() => {
                          const activeElement = document.activeElement;
                          if (activeElement && activeElement.blur) {
                            activeElement.blur();
                          }
                          // Remove foco de qualquer elemento com classe do react-select
                          const selectElements = document.querySelectorAll('[class*="css-"]');
                          selectElements.forEach(el => {
                            if (el.blur) el.blur();
                          });
                        }, 150);
                      }}
                      options={gestores}
                      placeholder={gestores.length === 1 ? "Selecionado automaticamente..." : "Selecione o gestor..."}
                      className="text-sm"
                      isDisabled={gestores.length === 1}
                      isClearable={gestores.length > 1}
                      menuPortalTarget={document.body}
                      styles={gestores.length > 1 ? gestorSelectStyles : {
                        ...gestorSelectStyles,
                        control: (base, state) => ({
                          ...gestorSelectStyles.control(base, state),
                          backgroundColor: '#f9fafb',
                          cursor: 'not-allowed'
                        })
                      }}
                    />
                  </div>

                  {!isNovoColaborador ? (
                    <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-300 p-5 shadow-sm form-field transition-all duration-150 ease-out">
                      <label className="block text-base font-medium text-blue-600 mb-2.5 flex items-center">
                        <svg className="w-4 h-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Nome <span className="text-red-500 ml-1 text-sm">*</span>
                      </label>
                      <Select
                        value={selectedFuncionario}
                        onChange={(value) => {
                          handleFuncionarioChange(value);
                          // Remove seleção do campo após seleção
                          setTimeout(() => {
                            const activeElement = document.activeElement;
                            if (activeElement && activeElement.blur) {
                              activeElement.blur();
                            }
                            // Remove foco de qualquer elemento com classe do react-select
                            const selectElements = document.querySelectorAll('[class*="css-"]');
                            selectElements.forEach(el => {
                              if (el.blur) el.blur();
                            });
                          }, 150);
                        }}
                        options={funcionarios}
                        placeholder="Digite para buscar..."
                        className="text-sm"
                        isDisabled={!selectedSetor}
                        isClearable
                        isSearchable
                        menuPortalTarget={document.body}
                        styles={nomeSelectStyles}
                      />
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-300 p-5 shadow-sm form-field transition-all duration-150 ease-out">
                      <label className="block text-base font-medium text-blue-600 mb-2.5 flex items-center">
                        <svg className="w-4 h-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Novo Colaborador <span className="text-red-500 ml-1 text-sm">*</span>
                      </label>
                      <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4 shadow-sm min-h-[48px] flex items-center">
                        <p className="text-base font-semibold text-blue-900 font-mono">
                          {codigoQLPGerado || 'Aguardando setor...'}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-300 p-5 shadow-sm form-field transition-all duration-150 ease-out">
                    <label className="block text-base font-medium text-blue-600 mb-2.5 flex items-center">
                      <svg className="w-4 h-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z" />
                      </svg>
                      Cargo <span className="text-red-500 ml-1 text-sm">*</span>
                    </label>
                    <Select
                      value={selectedCargo}
                      onChange={(value) => {
                        setSelectedCargo(value);
                        // Remove seleção do campo após seleção
                        setTimeout(() => {
                          const activeElement = document.activeElement;
                          if (activeElement && activeElement.blur) {
                            activeElement.blur();
                          }
                          // Remove foco de qualquer elemento com classe do react-select
                          const selectElements = document.querySelectorAll('[class*="css-"]');
                          selectElements.forEach(el => {
                            if (el.blur) el.blur();
                          });
                        }, 150);
                      }}
                      options={cargos}
                      placeholder="Selecione o cargo..."
                      className="text-sm"
                      isDisabled={!selectedSetor || (!isNovoColaborador && !selectedFuncionario)}
                      isClearable={isNovoColaborador}
                      isSearchable
                      menuPortalTarget={document.body}
                      styles={cargoSelectStyles}
                    />
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-300 p-5 shadow-sm form-field transition-all duration-150 ease-out">
                    <label className="block text-base font-medium text-blue-600 mb-2.5 flex items-center">
                      <svg className="w-4 h-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      Centro de Custo <span className="text-red-500 ml-1 text-sm">*</span>
                    </label>
                    <input
                      type="text"
                      value={centroDeCusto}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                        setCentroDeCusto(value);
                      }}
                      onClick={(e) => e.target.select()}
                      onFocus={(e) => e.target.select()}
                      maxLength={4}
                      className="w-full px-4 py-2.5 border border-transparent rounded-lg hover:border-blue-300 transition text-base font-mono bg-gray-50 min-h-[44px] focus:outline-none focus:ring-0 focus:border-blue-300"
                      placeholder={
                        !isNovoColaborador && selectedFuncionario && funcionarios.find(f => f.value === selectedFuncionario.value)?.centroDeCusto
                          ? funcionarios.find(f => f.value === selectedFuncionario.value).centroDeCusto
                          : "0000"
                      }
                    />
                    {centroDeCusto && (centroDeCusto.length !== 4 || !/^\d+$/.test(centroDeCusto)) && (
                      <p className="text-red-500 text-sm mt-2 flex items-center">
                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </p>
                    )}
              </div>

                  {/* Resumo Financeiro Expandido */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded p-3 mt-2">
                <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-blue-800">Resumo Financeiro</h4>
                  <div className="text-xs text-blue-600 font-medium">
                        {Object.keys(selectedItens).length} item(s)
                  </div>
                </div>

                <div className="space-y-2">
                  {/* Equipamentos */}
                    {totais.custoEquipamentos > 0 && (
                        <div className="bg-white rounded p-2 border border-gray-200">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                              <span className="text-xs font-medium text-gray-700">Equipamentos</span>
                        </div>
                            <span className="text-xs font-semibold text-gray-900">{formatCurrency(totais.custoEquipamentos)}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Investimento único</div>
                      </div>
                    )}

                  {/* Licenças - Custo Mensal */}
                    {totais.custoMensalLicencas > 0 && (
                        <div className="bg-white rounded p-2 border border-gray-200">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></div>
                              <span className="text-xs font-medium text-gray-700">Licenças (mensal)</span>
                        </div>
                            <span className="text-xs font-semibold text-gray-900">{formatCurrency(totais.custoMensalLicencas)}</span>
                        </div>
                      <div className="text-xs text-gray-500 mt-1">Custo mensal das licenças</div>
                    </div>
                  )}

                  {/* Licenças - Custo Anual */}
                  {totais.custoAnualLicencas > 0 && (
                        <div className="bg-white rounded p-2 border border-gray-200">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                              <span className="text-xs font-medium text-gray-700">Licenças (anual)</span>
                  </div>
                            <span className="text-xs font-semibold text-gray-900">{formatCurrency(totais.custoAnualLicencas)}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Custo anual das licenças</div>
                </div>
              )}

                  {/* Quando não há itens selecionados */}
                  {Object.keys(selectedItens).length === 0 && (
                        <div className="bg-gray-100 rounded p-3 text-center">
                          <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                          <p className="text-xs text-gray-600 font-medium">Nenhum item selecionado</p>
                      <p className="text-xs text-gray-500 mt-1">Selecione licenças e equipamentos para ver o resumo financeiro</p>
                    </div>
                  )}
                </div>

              </div>
            </div>
                  </div>
                </div>

            {/* COLUNA DIREITA - Recursos Solicitados (67%) */}
            <div className="lg:col-span-2 flex flex-col overflow-hidden animate-slide-in-right">
              <div className="p-4 flex flex-col overflow-hidden h-full">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recursos Solicitados</h3>

                <div className="space-y-6 flex-1 overflow-y-auto scrollbar-hide">
                  {/* Licenças ERP */}
                  {licencasERPList.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-base font-semibold text-red-800">Licenças ERP</h4>
                      </div>
                      <div className="space-y-2">
                        {licencasERPList.map(item => (
                          <div
                            key={item.id}
                            className={`border rounded erp-card transition-all duration-200 ease-out ${
                              selectedItens[item.id]?.selected
                                ? 'border-red-500 bg-red-50 shadow-md'
                                : 'border-gray-200 hover:border-red-300 bg-white hover:bg-red-50'
                            }`}
                          >
                            <div 
                              onClick={() => handleItemToggle(item.id, true)}
                              className="p-2 cursor-pointer"
                            >
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={selectedItens[item.id]?.selected || false}
                                  onChange={() => {}}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500 pointer-events-none"
                                />
                                <div className="ml-2 flex-1">
                                    <div className="flex justify-between items-start">
                                      <div className="flex items-center">
                                        <div>
                                          <span className="text-base font-normal text-slate-900">{item.nome}</span>
                                          {item.descricao && (
                                            <p className="text-sm text-gray-600 mt-1">{item.descricao}</p>
                                          )}
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <span className="text-base font-semibold text-red-600">
                                      {formatCurrency(item.preco)}/mês
                                    </span>
                                      </div>
                            </div>
                            
                                  <div className={`erp-permission-buttons overflow-hidden transition-all duration-300 ease-out ${
                                    selectedItens[item.id]?.selected ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'
                                  }`}>
            <div className="mt-2 pt-2 border-t border-red-200">
              <p className="text-xs font-medium text-red-700 mb-1">Nível de acesso:</p>
                                      <div className="flex gap-2">
                                        <label 
                                          className="flex items-center cursor-pointer bg-white hover:bg-red-50 border border-red-300 rounded px-2 py-1 transition-all duration-150 ease-out flex-1"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <input
                                            type="radio"
                                            name={`permissao-${item.id}`}
                                            checked={selectedItens[item.id]?.permissao === 'EDITAR'}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              handlePermissaoChange(item.id, 'EDITAR');
                                            }}
                                            className="w-3 h-3 text-red-600 border-red-300 focus:ring-red-500 cursor-pointer"
                                          />
                                          <div className="ml-1">
                                            <span className="text-xs font-medium text-red-700">Criar / Editar</span>
                                          </div>
                                        </label>
                                        <label 
                                          className="flex items-center cursor-pointer bg-white hover:bg-red-50 border border-red-300 rounded px-2 py-1 transition-all duration-150 ease-out flex-1"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <input
                                            type="radio"
                                            name={`permissao-${item.id}`}
                                            checked={selectedItens[item.id]?.permissao === 'VISUALIZAR'}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              handlePermissaoChange(item.id, 'VISUALIZAR');
                                            }}
                                            className="w-3 h-3 text-red-600 border-red-300 focus:ring-red-500 cursor-pointer"
                                          />
                                          <div className="ml-1">
                                            <span className="text-xs font-medium text-red-700">Visualizar</span>
                                          </div>
                                        </label>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Outras Licenças */}
                  {outrasLicencas.length > 0 && (
                    <div>
                      <h4 className="text-base font-semibold text-green-800 mb-3">Outras Licenças</h4>
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                        {outrasLicencas.map(item => (
                          <div
                            key={item.id}
                            onClick={() => handleItemToggle(item.id, false)}
                            className={`flex items-center p-2 border rounded cursor-pointer license-card transition-all duration-150 ease-out ${
                              selectedItens[item.id]?.selected
                                ? 'border-green-500 bg-green-50 shadow-md'
                                : 'border-gray-200 hover:border-green-300 bg-white hover:bg-green-50'
                            }`}
                          >
                              <input
                                type="checkbox"
                                checked={selectedItens[item.id]?.selected || false}
                                onChange={() => {}}
                                onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 pointer-events-none flex-shrink-0"
                              />
                            <div className="ml-2 flex-1 min-w-0">
                              <p className="text-sm font-normal text-gray-900 truncate">{item.nome}</p>
                              {item.descricao && (
                                <p className="text-xs text-gray-500 mt-1 truncate">{item.descricao}</p>
                              )}
                              <p className="text-sm text-gray-600">{formatCurrency(item.preco)}/mês</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Equipamentos */}
                  {equipamentos.length > 0 && (
                    <div>
                      <h4 className="text-base font-semibold text-purple-800 mb-3">Equipamentos</h4>
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                        {equipamentos.map(item => (
                          <div
                            key={item.id}
                            onClick={() => handleItemToggle(item.id, false)}
                            className={`flex items-center p-2 border rounded cursor-pointer equipment-card transition-all duration-150 ease-out ${
                              selectedItens[item.id]?.selected
                                ? 'border-purple-500 bg-purple-50 shadow-md'
                                : 'border-gray-200 hover:border-purple-300 bg-white hover:bg-purple-50'
                            }`}
                          >
                              <input
                                type="checkbox"
                                checked={selectedItens[item.id]?.selected || false}
                                onChange={() => {}}
                                onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 pointer-events-none flex-shrink-0"
                            />
                            <div className="ml-2 flex-1 min-w-0">
                              <p className="text-sm font-normal text-gray-900 truncate">{item.nome}</p>
                              {item.descricao && (
                                <p className="text-xs text-gray-500 mt-1 truncate">{item.descricao}</p>
                              )}
                              <p className="text-sm text-gray-600">{formatCurrency(item.preco)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Botão de Submit */}
          <div className="bg-gray-50 border-t border-gray-200 p-4 animate-scale-in">
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className={`w-full py-3 px-6 rounded-lg font-semibold text-base transition-all duration-150 ease-out ${
                isFormValid && !isSubmitting
                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md transform hover:-translate-y-0.5'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processando...
                </span>
              ) : (
                'Enviar Solicitação'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SolicitationForm;
